// src/contexts/geral/LeadCaptacaoContext.js
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { listarLeadsCaptacao, obterLeadCaptacao } from '../../services/geral/LeadCaptacaoService';
import { useMessage } from './MessageContext';

const LeadCaptacaoContext = createContext();
export const useLeadCaptacao = () => useContext(LeadCaptacaoContext);

export const LeadCaptacaoProvider = ({ children }) => {
  const { showMessage } = useMessage();
  const [resultado, setResultado] = useState({ itens: [], total: 0, pagina: 1, tamanhoPagina: 20 });
  const [filtrosAplicados, setFiltrosAplicados] = useState({ busca: '', intencao: '' });
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [detalhesAberto, setDetalhesAberto] = useState(false);
  const [selecionado, setSelecionado] = useState(null);
  const [carregandoDetalhes, setCarregandoDetalhes] = useState(false);
  const [erroDetalhes, setErroDetalhes] = useState('');
  const consultaAtual = useRef(0);
  const detalheAtual = useRef(0);

  useEffect(() => () => {
    // Descartar respostas de consultas encerradas ou de um módulo desabilitado.
    consultaAtual.current += 1;
    detalheAtual.current += 1;
  }, []);

  const consultar = useCallback(async parametros => {
    const consulta = ++consultaAtual.current;
    setCarregando(true);
    setErro('');
    setFiltrosAplicados({ busca: parametros.busca, intencao: parametros.intencao });
    try {
      const pagina = await listarLeadsCaptacao(parametros);
      if (consulta === consultaAtual.current) setResultado(pagina);
    } catch (error) {
      if (consulta !== consultaAtual.current) return;
      const mensagem = error?.message || 'Não foi possível consultar os leads. Tente novamente.';
      setErro(mensagem);
      setResultado({ itens: [], total: 0, pagina: parametros.pagina, tamanhoPagina: parametros.tamanhoPagina });
      showMessage(mensagem, 'danger');
    } finally {
      if (consulta === consultaAtual.current) setCarregando(false);
    }
  }, [showMessage]);

  const buscar = useCallback((filtros = {}, tamanhoPagina = resultado.tamanhoPagina) => consultar({
    busca: (filtros.busca || '').trim(), intencao: filtros.intencao || '', pagina: 1, tamanhoPagina,
  }), [consultar, resultado.tamanhoPagina]);

  useEffect(() => {
    consultar({ busca: '', intencao: '', pagina: 1, tamanhoPagina: 20 });
  }, [consultar]);

  const mudarPagina = pagina => consultar({ ...filtrosAplicados, pagina, tamanhoPagina: resultado.tamanhoPagina });
  const mudarTamanhoPagina = tamanhoPagina => consultar({ ...filtrosAplicados, pagina: 1, tamanhoPagina });

  const fecharDetalhes = () => {
    detalheAtual.current += 1;
    setDetalhesAberto(false);
    setSelecionado(null);
    setCarregandoDetalhes(false);
    setErroDetalhes('');
  };

  const abrirDetalhes = async id => {
    const consulta = ++detalheAtual.current;
    setSelecionado(null);
    setErroDetalhes('');
    setDetalhesAberto(true);
    setCarregandoDetalhes(true);
    try {
      const lead = await obterLeadCaptacao(id);
      if (consulta === detalheAtual.current) setSelecionado(lead);
    } catch (error) {
      if (consulta !== detalheAtual.current) return;
      const mensagem = error?.status === 404 ? 'Lead não encontrado. Atualize a consulta.'
        : error?.message || 'Não foi possível carregar os detalhes do lead.';
      setErroDetalhes(mensagem);
      showMessage(mensagem, 'danger');
    } finally {
      if (consulta === detalheAtual.current) setCarregandoDetalhes(false);
    }
  };

  return (
    <LeadCaptacaoContext.Provider value={{
      resultado, carregando, erro, buscar, mudarPagina, mudarTamanhoPagina,
      detalhesAberto, selecionado, carregandoDetalhes, erroDetalhes, abrirDetalhes, fecharDetalhes,
    }}>
      {children}
    </LeadCaptacaoContext.Provider>
  );
};
